import { useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/layout/PageHeader";
import { cn } from "@/design-system/lib/utils";
import type { ServiceProviderType } from "@/types/serviceProviders";
import { PROVIDER_CONFIGS, PROVIDER_ORDER } from "./providerConfig";
import { ServiceProviderListView } from "./ServiceProviderListView";

function isProviderType(value: string | null): value is ServiceProviderType {
  return value !== null && value in PROVIDER_CONFIGS;
}

export function ServiceProvidersLandingPage() {
  const [params, setParams] = useSearchParams();
  const typeParam = params.get("type");
  const activeType: ServiceProviderType = isProviderType(typeParam)
    ? typeParam
    : "IndentureTrustee";

  const setType = (next: ServiceProviderType) => {
    const updated = new URLSearchParams(params);
    updated.set("type", next);
    setParams(updated, { replace: true });
  };

  const config = PROVIDER_CONFIGS[activeType];

  return (
    <>
      <PageHeader
        eyebrow="Capital Markets"
        title="Service Providers"
        description="Indenture trustees, trust companies, placement agents and backup servicers."
      />

      <div
        role="tablist"
        aria-label="Service provider types"
        className="flex flex-wrap gap-space-1 border-b border-border-light mb-space-4"
      >
        {PROVIDER_ORDER.map((type) => {
          const cfg = PROVIDER_CONFIGS[type];
          const active = type === activeType;
          return (
            <button
              key={type}
              type="button"
              role="tab"
              aria-selected={active}
              data-testid={`provider-tab-${type}`}
              onClick={() => setType(type)}
              className={cn(
                "px-space-4 py-space-2 text-small font-display font-semibold tracking-wide",
                "border-b-2 -mb-px transition-colors duration-fast",
                active
                  ? "border-gold text-onyx"
                  : "border-transparent text-gray-mid hover:text-onyx",
              )}
            >
              {cfg.pluralLabel}
            </button>
          );
        })}
      </div>

      <ServiceProviderListView key={activeType} config={config} />
    </>
  );
}
